import { TListResponse } from '.';
import {
  Equipment,
  TClientId,
  TEquipmentId,
  TEquipmentTypeId,
  TFSERegistrationStatusId,
  TFacilityId,
  TFuelPathwayId,
} from '/src/lib/models';
import { TChargingNetworkId } from '/src/lib/models/ChargingNetwork';
import { TChargingTypeId } from '/src/lib/models/ChargingType';
import { TEquipmentUseCaseId } from '/src/lib/models/EquipmentUseCase';
import { TForkliftClassificationId } from '/src/lib/models/ForkliftClassification';
import { TRetirementReasonId } from '/src/lib/models/RetirementReason';
import { DateString, FloatString, Nullable, TResponseMetadata } from '/src/lib/types';

/**
 * List Equipment by Facility
 */
export type TListEquipmentRequest = {
  facility_id: TFacilityId;
  page?: number;
  limit?: number;
  is_active?: boolean;
  is_metered?: boolean;
  is_finalized?: boolean;
  active_by_reporting_quarter?: DateString;
  equipment_type_id?: TEquipmentTypeId;
  name?: string;
  unit_number?: string;
  serial_number?: string;
  manufacturer?: string;
  model_number?: string;
  model_year?: number;
  fse_registration_status_id?: TFSERegistrationStatusId;
  fse_id?: string;
  fse_ru?: string;
};

export type TListEquipmentResponse = TListResponse<Equipment[]> & {
  earliest_first_active_reporting_quarter?: Nullable<DateString>;
};

/**
 * List Equipment by Client
 */
export type TListClientEquipmentRequest = {
  client_id: TClientId;
  page?: number;
  limit?: number;
  is_active?: boolean;
  is_metered?: boolean;
  equipment_type_id?: TEquipmentTypeId;
  name?: string;
  unit_number?: string;
  serial_number?: string;
  manufacturer?: string;
  model_number?: string;
  model_year?: number;
  fse_registration_status_id?: TFSERegistrationStatusId;
  fse_id?: string;
  fse_ru?: string;
};

export type TListClientEquipmentResponse = TListResponse<Equipment[]>;

/**
 * List Metered Equipment
 */
export type TListMeteredEquipmentRequest = {
  facility_id: TFacilityId;
};

export type TListMeteredEquipmentResponse = {
  meta: TResponseMetadata;
  data: Equipment[];
};

/**
 * List All Equipment
 */
export type TListAllEquipmentRequest = {
  page?: number;
  limit?: number;
  is_active?: boolean;
  is_metered?: boolean;
  equipment_type_id?: TEquipmentTypeId;
  fuel_pathway_id?: TFuelPathwayId;
  name?: string;
  unit_number?: string;
  serial_number?: string;
  manufacturer?: string;
  model_number?: string;
  model_year?: number;
  fse_registration_status_id?: TFSERegistrationStatusId;
  fse_id?: string;
  fse_ru?: string;
};

export type TListAllEquipmentResponse = TListResponse<Equipment[]>;

/**
 * Get Equipment
 */
export type TGetEquipmentRequest = {
  id: TEquipmentId;
};

export type TGetEquipmentResponse = Equipment;

/**
 * Create Equipment - Internal Users
 */
export type TCreateEquipmentInternalRequest = {
  facility_id: TFacilityId;
  equipment_type_id: TEquipmentTypeId;
  fse_registration_status_id: TFSERegistrationStatusId;
  first_active_reporting_quarter: Nullable<DateString>;
  fse_ru: Nullable<string>;
  fse_id: Nullable<string>;
  manufacturer: string;
  model_number: Nullable<string>;
  model_year: number;
  serial_number: string;
  unit_number: Nullable<string>;
  first_day_in_service: Nullable<DateString>;
  is_metered: boolean;
  latitude: FloatString;
  longitude: FloatString;
  comments: Nullable<string>;
  charging_network_id: Nullable<TChargingNetworkId>;
  equipment_use_case_id: Nullable<TEquipmentUseCaseId>;
  charging_type_id: Nullable<TChargingTypeId>;
  nameplate_rating_kw: Nullable<number>;
  forklift_classification_id: Nullable<TForkliftClassificationId>;
};

export type TCreateEquipmentInternalResponse = Equipment;

/**
 * Create Equipment - External Users
 */
export type TCreateEquipmentExternalRequest = {
  facility_id: TFacilityId;
  equipment_type_id: TEquipmentTypeId;
  first_active_reporting_quarter: Nullable<DateString>;
  manufacturer: string;
  model_number: Nullable<string>;
  model_year: number;
  serial_number: string;
  unit_number: Nullable<string>;
  first_day_in_service: Nullable<DateString>;
  is_metered: boolean;
  latitude: FloatString;
  longitude: FloatString;
  comments: Nullable<string>;
  charging_network_id: Nullable<TChargingNetworkId>;
  equipment_use_case_id: Nullable<TEquipmentUseCaseId>;
  charging_type_id: Nullable<TChargingTypeId>;
  nameplate_rating_kw: Nullable<number>;
  forklift_classification_id: Nullable<TForkliftClassificationId>;
};

export type TCreateEquipmentExternalResponse = Equipment;

/**
 * Update NonFinalized Equipment - Internal Users
 */
export type TUpdateNonFinalizedEquipmentInternalRequest = {
  id: TEquipmentId;
  equipment_type_id: TEquipmentTypeId;
  fse_registration_status_id: TFSERegistrationStatusId;
  first_active_reporting_quarter: Nullable<DateString>;
  fse_id: Nullable<string>;
  fse_ru: Nullable<string>;
  manufacturer: string;
  model_number: Nullable<string>;
  model_year: number;
  serial_number: string;
  unit_number: Nullable<string>;
  first_day_in_service: Nullable<DateString>;
  is_metered: boolean;
  latitude: FloatString;
  longitude: FloatString;
  comments: Nullable<string>;
  charging_network_id: Nullable<TChargingNetworkId>;
  equipment_use_case_id: Nullable<TEquipmentUseCaseId>;
  charging_type_id: Nullable<TChargingTypeId>;
  nameplate_rating_kw: Nullable<number>;
  forklift_classification_id: Nullable<TForkliftClassificationId>;
};

export type TUpdateNonFinalizedEquipmentInternalResponse = Equipment;

/**
 * Update Finalized Equipment - Internal Users
 */
export type TUpdateFinalizedEquipmentInternalRequest = {
  id: TEquipmentId;
  latitude: FloatString;
  longitude: FloatString;
  unit_number: Nullable<string>;
  comments: Nullable<string>;
};

export type TUpdateFinalizedEquipmentInternalResponse = Equipment;

/**
 * Update Pending Equipment - External Users
 */
export type TUpdatePendingEquipmentExternalRequest = {
  id: TEquipmentId;
  equipment_type_id: TEquipmentTypeId;
  first_active_reporting_quarter: Nullable<DateString>;
  manufacturer: string;
  model_number: Nullable<string>;
  model_year: number;
  serial_number: string;
  unit_number: Nullable<string>;
  first_day_in_service: Nullable<DateString>;
  is_metered: boolean;
  latitude: FloatString;
  longitude: FloatString;
  comments: Nullable<string>;
  charging_network_id: Nullable<TChargingNetworkId>;
  equipment_use_case_id: Nullable<TEquipmentUseCaseId>;
  charging_type_id: Nullable<TChargingTypeId>;
  nameplate_rating_kw: Nullable<number>;
  forklift_classification_id: Nullable<TForkliftClassificationId>;
};

export type TUpdatePendingEquipmentExternalResponse = Equipment;

/**
 * Update Non-Pending Equipment - External Users
 */
export type TUpdateNonPendingEquipmentExternalRequest = {
  id: TEquipmentId;
  latitude: FloatString;
  longitude: FloatString;
  unit_number: Nullable<string>;
  comments: Nullable<string>;
};

export type TUpdateNonPendingEquipmentExternalResponse = Equipment;

/**
 * Retire Equipment
 */
export type TRetireEquipmentRequest = {
  equipment_ids: TEquipmentId[];
  last_active_reporting_quarter: DateString;
  retirement_reason_id: TRetirementReasonId;
  retirement_reason_other?: Nullable<string>;
};

/**
 * Delete Equipment
 */
export type TDeleteEquipmentRequest = {
  equipment_ids: TEquipmentId[];
};
